import React, { useState, useEffect } from 'react';
import { Lock, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { PecktreeLogo } from '../shared/PecktreeLogo.tsx';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }
    if (!token) {
      setError(t('resetPasswordInvalid'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await ApiService.resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t('resetPasswordInvalid'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="auth-success-state">
            <div className="w-16 h-16 bg-pt-ruby/10 dark:bg-pt-ruby-light/10 rounded-full flex items-center justify-center">
              <AlertCircle className="text-pt-ruby dark:text-pt-ruby-light" size={32} />
            </div>
            <h2 className="auth-success-title">{t('resetPasswordInvalid')}</h2>
            <a href="/" className="auth-link-btn">
              <ArrowLeft size={14} />
              {t('backToLogin')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        {/* Brand */}
        <div className="reset-password-brand">
          <PecktreeLogo size={44} />
          <span className="auth-wordmark">
            <span className="auth-wordmark-peck">Peck</span>
            <span className="auth-wordmark-tree">tree</span>
          </span>
        </div>

        {success ? (
          <div className="auth-success-state">
            <div className="auth-success-icon">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h3 className="auth-success-title">{t('resetPasswordSuccess')}</h3>
            <p className="auth-success-desc">{t('resetPasswordSuccessDesc')}</p>
            <a href="/" className="auth-link-btn">
              <ArrowLeft size={14} />
              {t('backToLogin')}
            </a>
          </div>
        ) : (
          <>
            <div className="auth-form-header">
              <h2 className="auth-form-title">{t('resetPasswordTitle')}</h2>
              <p className="auth-form-subtitle">{t('resetPasswordDesc')}</p>
            </div>

            {error && (
              <div className="auth-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-field">
                <label htmlFor="new-password" className="auth-label">
                  {t('newPassword')}
                </label>
                <div className="auth-input-wrap">
                  <Lock className="auth-input-icon" size={16} />
                  <input
                    id="new-password"
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="auth-input auth-input--password"
                    placeholder="••••••••"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="confirm-password" className="auth-label">
                  {t('confirmPassword')}
                </label>
                <div className="auth-input-wrap">
                  <Lock className="auth-input-icon" size={16} />
                  <input
                    id="confirm-password"
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="auth-input"
                    placeholder="••••••••"
                    minLength={8}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="auth-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
                {t('resetPasswordSubmit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
